import { useMemo } from "react";
import { useThemeSlice } from "../../components/ui/StatusBadge.jsx";
import { EntityLoadingState } from "../../components/ui/EntityPageChrome.jsx";
import { compactPrimaryButton } from "../../components/ui/buttonStyles.js";
import {
  PageErrorBanner,
  TypeCatalogCard,
  TypeCatalogListFooter,
  TypeCatalogSectionHeader,
  TypeCatalogToolbar,
} from "../../components/ui/TypeCatalogToolbar.jsx";
import { TypeCatalogEmptyState } from "../../components/emptyStates/TypeCatalogEmptyState.jsx";
import { DeleteConfirmModal } from "../../components/modal/DeleteConfirmModal.jsx";
import { Ico } from "../../components/icons/icons.jsx";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm.js";
import { useDeviceTypes } from "../../logic/hooks/useDeviceTypes.js";
import { buildAccountDetailTokens } from "../Accounts/accountDetailTheme.js";
import { DeviceTypeList } from "./components/DeviceTypeList.jsx";
import { DeviceTypeModal } from "./components/DeviceTypeModal.jsx";

export function DeviceTypesPage() {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const vm = useDeviceTypes();
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  const showing = vm.items.length;
  const total = vm.allItems.length;
  const filtered = vm.search.trim().length > 0;
  const countLabel = filtered ? `${showing} of ${total} types` : `${total} type${total === 1 ? "" : "s"}`;

  const handleDelete = (row) => {
    requestDelete({
      title: "Delete device type",
      description: "This removes the hardware model definition. Existing devices using this type are not affected in this demo.",
      confirmText: row.name,
      entityLabel: "Device type",
      onConfirm: () => vm.removeDeviceType(row.id),
    });
  };

  const createAction = (
    <button type="button" onClick={vm.openCreate} style={compactPrimaryButton(A)}>
      {Ico.plus}
      Create device type
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden", padding: "14px" }}>
      <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
        <TypeCatalogSectionHeader
          title="Device types"
          subtitle="Define hardware model definitions — each type specifies manufacturer, protocol, and custom telemetry fields."
          action={createAction}
          c={c}
        />

        <PageErrorBanner message={vm.error} />

        <TypeCatalogCard ap={ap} dark={dark}>
          <TypeCatalogToolbar search={vm.search} onSearchChange={vm.setSearch} placeholder="Search device types…" countLabel={countLabel} c={c} />

          {vm.loading ? (
            <EntityLoadingState />
          ) : total === 0 ? (
            <TypeCatalogEmptyState
              c={c}
              A={A}
              icon={Ico.model}
              message="No device types yet"
              hint="Create a type to define hardware models like GPS trackers or CAN gateways."
              onAction={vm.openCreate}
              actionLabel="Create device type"
            />
          ) : showing === 0 ? (
            <TypeCatalogEmptyState c={c} A={A} icon={Ico.model} message="No matching device types" hint="Try a different search term." />
          ) : (
            <DeviceTypeList items={vm.items} ap={ap} onEdit={vm.openEdit} onDelete={handleDelete} />
          )}

          {!vm.loading && showing > 0 && (
            <TypeCatalogListFooter c={c}>
              Showing {showing} device type{showing === 1 ? "" : "s"}
              {filtered ? ` matching “${vm.search.trim()}”` : ""}
            </TypeCatalogListFooter>
          )}
        </TypeCatalogCard>

        <DeviceTypeModal open={vm.modalOpen} onClose={vm.closeModal} onSave={vm.saveDeviceType} editing={vm.editing} saving={vm.saving} />
        <DeleteConfirmModal {...deleteModalProps} />
      </div>
    </div>
  );
}
